// ====================================================== //
// Article.js (Markdown-enabled)                           //
//   • Autolinks + [label](url) via react-markdown + GFM   //
//   • Sanitized via rehype-sanitize                       //
//   • External links open in new tab                      //
//   • Fixes invalid <p> nesting in meta                   //
// ====================================================== //
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import "./Article.css";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { API_BASE } from "../config";

/* ---------- Helpers ---------------------------------------------- */
const ensureHttp = (href = "") => {
  if (!href) return href;
  if (
    href.startsWith("/") ||
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    /^https?:\/\//i.test(href)
  ) {
    return href;
  }
  if (href.startsWith("www.")) return `https://${href}`;
  return href;
};

const parseMaybeJson = (v) => {
  if (!v) return null;
  if (typeof v === "string") {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v;
};

// --- helpers (replace your getArticleCropStyles with this) ---
function getArticleCropStyles(imageJson) {
  let j = parseMaybeJson(imageJson);
  if (Array.isArray(j)) j = j[0] ?? null;
  if (!j || typeof j !== "object") return null;

  const iw = Number(j?.original?.width);
  const ih = Number(j?.original?.height);
  if (!isFinite(iw) || !isFinite(ih) || iw <= 0 || ih <= 0) return null;

  const baseFrame = {
    position: "relative",
    width: "100%",   // stay inside article width
    overflow: "hidden",
    borderRadius: 8,
    display: "block",
    height: 0,       // aspect via padding-bottom
  };

  const ac = j?.articleCrop;
  const enabled =
    ac &&
    !(ac.enabled === false ||
      String(ac.enabled).toLowerCase() === "false" ||
      String(ac.enabled) === "0");

  // No crop → show the whole image at its natural aspect, column width
  if (!ac || !enabled || !ac.box) {
    const aspect = iw / ih;
    return {
      frame: { ...baseFrame, paddingBottom: `${100 / aspect}%` },
      img: {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover", // same aspect as frame → no cropping
      },
    };
  }

  // Crop active (box values are 0..1 of the original)
  let x = Number(ac.box.x);
  let y = Number(ac.box.y);
  let width = Number(ac.box.width);
  let height = Number(ac.box.height);
  if (![x, y, width, height].every(n => isFinite(n))) return null;

  const clamp01 = v => Math.max(0, Math.min(1, v));
  width = Math.max(0.0001, clamp01(width));
  height = Math.max(0.0001, clamp01(height));
  x = clamp01(x);
  y = clamp01(y);

  // Visible aspect = (iw*width)/(ih*height)
  const cropAspect = (iw / ih) * (width / height);

  return {
    // Frame width is 100% of article; height derives from the crop aspect
    frame: { ...baseFrame, paddingBottom: `${100 / cropAspect}%` },
    // Scale & offset the original so the crop fills the frame
    img: {
      position: "absolute",
      display: "block",
      width: `${(1 / width) * 100}%`,
      height: "auto",
      left: `-${(x / width) * 100}%`,
      top: `-${(y / height) * 100}%`,
      maxWidth: "none", // defeat any global img { max-width:100% }
      userSelect: "none",
      pointerEvents: "none",
    },
  };
}


const ImageBlock = ({ src, alt, cropJson }) => {
  const styles = getArticleCropStyles(cropJson);
  if (!styles) {
    return (
      <div className="article-img">
        <img src={src} alt={alt || ""} style={{ width: "100%", height: "auto", display: "block" }} />
      </div>
    );
  }
  return (
    <div className="article-img">
      <div className="article-crop-frame" style={styles.frame}>
        <img className="article-crop-img" src={src} alt={alt || ""} style={styles.img} />
      </div>
    </div>
  );
};






/* ---------- Sub-components ---------------------------------------- */
const TextBlock = ({ body }) => {
  const components = {
    // Style paragraphs like your old <p className="article-text">
    p: ({ node, ...props }) => <p className="article-text" {...props} />,
    a: ({ node, href, children, ...props }) => {
      const safeHref = ensureHttp(href || "");
      let isExternal = false;
      try {
        const url = new URL(safeHref, window.location.href);
        isExternal =
          /^https?:$/i.test(url.protocol) &&
          url.origin !== window.location.origin;
      } catch {
        // If URL parsing fails, treat as internal/relative
        isExternal = false;
      }
      return (
        <a
          href={safeHref}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          {...props}
        >
          {children}
        </a>
      );
    },
    // Optional: make inline images in markdown behave nicely
    img: ({ node, alt, src, ...props }) => (
      <img src={src} alt={alt || ""} loading="lazy" {...props} />
    ),
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}        // autolink bare URLs, tables, strikethrough, etc.
      rehypePlugins={[rehypeSanitize]}   // sanitize any embedded HTML
      components={components}
    >
      {String(body || "")}
    </ReactMarkdown>
  );
};



const QuoteBlock = ({ body, source }) => (
  <blockquote className="article-quote">
    <p className="article-quote-body">{body}</p>
    {source && <footer className="article-quote-source">— {source}</footer>}
  </blockquote>
);

/* ---------- Renderer ---------------------------------------------- */
const renderBlock = (block, idx) => {
  switch (block.type) {
    case "text":
      return <TextBlock key={idx} body={block.body_md || block.body || ""} />;
    case "image":
      return (
        <ImageBlock
          key={idx}
          src={block.path || block.src}
          alt={block.alt_text}
          cropJson={block.crop_json || block.image_json}
          fullBleed={block.full_bleed}
        />
      );

    case "quote":
      return (
        <QuoteBlock
          key={idx}
          body={block.body_md || block.body || ""}
          source={block.source}
        />
      );
    default:
      return null;
  }
};

/* ---------- Page component ---------------------------------------- */
const Article = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/news/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        setArticle(data);
        setErr(null);
      })
      .catch((e) => setErr(e))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p style={{ textAlign: "center" }}>Loading …</p>;
  if (err) return <p style={{ color: "red", textAlign: "center" }}>{err}</p>;
  if (!article) return null;

  const dateStr = new Date(article.published_at).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="page-wrapper article-wrapper">
      <Header />

      <div className="page-content">
        <article className="page-container">
          <h1 className="article-title">{article.title}</h1>

          <div className="article-meta">
            <span className="article-date">{dateStr}</span>
            {article.category && (
              <span className="article-category">{article.category}</span>
            )}
          </div>

          <div className="article-text">
            {article.blocks && article.blocks.map((block, idx) => renderBlock(block, idx))}
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default Article;
